import { useId, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  apiErrorText,
  useActivateEconDictMutation,
  useCreateEconDictMutation,
  useDeactivateEconDictMutation,
  useUpdateEconDictMutation,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerFooter,
  DrawerHeader,
} from '@shared/ui/Drawer';
import { Select } from '@shared/ui/Input';
import {
  Actions,
  CheckLabel,
  Control,
  ControlArea,
  FieldError,
  FieldHint,
  FieldLabel,
  FleetChip,
  FooterStatus,
  FormGrid,
  FormRow,
  Notice,
  Spacer,
  typo,
} from '@entities/fleet';
import { ECON_DICTIONARIES } from '@entities/economic/dictionaryTypes';
import { DrawerTitle, Eyebrow, HeaderMeta, HeaderRow, Titles } from '../prc/drawerParts';
import { RuleBox } from './styles';
import {
  buildBody,
  changedCount,
  DICT_FIELDS,
  DICT_UI,
  dictIssues,
  distributionHint,
  emptyValues,
  fieldIssues,
  HAS_MATCH_RULE,
  isWideField,
  MATCH_RULE_HINTS,
  statusLabel,
  valuesFromRecord,
  type DictRecord,
  type FieldDef,
  type FormValues,
  type LineOption,
  type SelectOption,
  type TableDict,
} from './types';

/** Варианты списка; с группами — под заголовками групп в порядке списка. */
function renderOptions(options: readonly SelectOption[]) {
  const option = (o: SelectOption) => (
    <option key={o.value} value={o.value}>
      {o.label}
    </option>
  );
  if (!options.some((o) => o.group)) return options.map(option);
  const groups: { label: string; items: SelectOption[] }[] = [];
  for (const o of options) {
    const label = o.group ?? '';
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(o);
    else groups.push({ label, items: [o] });
  }
  return groups.map((g) => (
    <optgroup key={g.label} label={g.label}>
      {g.items.map(option)}
    </optgroup>
  ));
}

/** Правило записи для поля формы: непустой объект — JSON с отступами. */
function ruleText(rec: DictRecord | null): string {
  const rule = rec?.match_rule;
  return rule && typeof rule === 'object' && Object.keys(rule).length > 0
    ? JSON.stringify(rule, null, 2)
    : '';
}

/**
 * Запись справочника в боковой панели: новая (`record = null`) или
 * существующая. Поля — по схеме справочника, ошибка — под своим полем.
 * Без права записи панель только показывает запись.
 */
export function DictDrawer({
  dict,
  record,
  canWrite,
  projectOptions,
  lineOptions,
  onClose,
  onSaved,
}: {
  dict: TableDict;
  record: DictRecord | null;
  canWrite: boolean;
  /** Проекты ПТО для связки объекта. */
  projectOptions: readonly { value: string; label: string }[];
  /** Строки БДР для статьи затрат 1С. */
  lineOptions: readonly LineOption[];
  onClose: () => void;
  /** Сохранено: что сказать над списком. */
  onSaved: (message: string) => void;
}) {
  const uid = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const ui = DICT_UI[dict];
  const fields = DICT_FIELDS[dict];
  const hasRule = HAS_MATCH_RULE.has(dict);
  const dictTitle = ECON_DICTIONARIES.find((d) => d.id === dict)?.title ?? '';

  const initial = useMemo<FormValues>(
    () => (record ? valuesFromRecord(fields, record) : emptyValues(fields)),
    [record, fields],
  );
  const initialRule = useMemo(() => ruleText(record), [record]);
  const [values, setValues] = useState<FormValues>(initial);
  const [rule, setRule] = useState(initialRule);
  const [showIssues, setShowIssues] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [create, createState] = useCreateEconDictMutation();
  const [update, updateState] = useUpdateEconDictMutation();
  const [activate, activateState] = useActivateEconDictMutation();
  const [deactivate, deactivateState] = useDeactivateEconDictMutation();
  const busy =
    createState.isLoading ||
    updateState.isLoading ||
    activateState.isLoading ||
    deactivateState.isLoading;

  // Строка статьи, которую потом выключили, в списке не значится — показываем
  // её, чтобы правка примечания не выглядела как потеря строки.
  const lines = useMemo<readonly SelectOption[]>(() => {
    const id = typeof record?.bdr_article_id === 'string' ? record.bdr_article_id : '';
    if (!id || lineOptions.length === 0 || lineOptions.some((o) => o.value === id)) {
      return lineOptions;
    }
    const name = typeof record?.bdr_article_name === 'string' ? record.bdr_article_name : id;
    return [{ value: id, label: `${name} — строка выключена` }, ...lineOptions];
  }, [record, lineOptions]);

  const issues = useMemo(
    () => ({ ...fieldIssues(fields, values), ...dictIssues(dict, values) }),
    [dict, fields, values],
  );
  const dirty = changedCount(initial, values) + (rule !== initialRule ? 1 : 0);
  const editable = canWrite;
  const active = record?.is_active !== false;
  const title = record ? ui.recordTitle(record) : ui.newTitle;

  function close() {
    if (dirty > 0 && !busy && !window.confirm('Закрыть без сохранения? Изменения пропадут.')) {
      return;
    }
    onClose();
  }

  function set(name: string, value: string | boolean) {
    setValues((v) => ({ ...v, [name]: value }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (Object.keys(issues).length > 0) {
      setShowIssues(true);
      const first = fields.find((f) => issues[f.name]);
      if (first) document.getElementById(`${uid}-${first.name}`)?.focus();
      return;
    }
    const { body, error: bodyError } = buildBody(fields, values, hasRule ? rule : '');
    if (bodyError || !body) {
      setError(bodyError ?? 'Проверьте поля формы.');
      return;
    }
    try {
      if (record) {
        await update({ dict, id: String(record.id), body }).unwrap();
        onSaved(`${title}: изменения сохранены.`);
      } else {
        const res = await create({ dict, body }).unwrap();
        const item =
          res.item && typeof res.item === 'object'
            ? (res.item as DictRecord)
            : (body as DictRecord);
        onSaved(`${ui.recordTitle(item)} ${ui.feminine ? 'добавлена' : 'добавлен'}.`);
      }
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось сохранить запись.'));
    }
  }

  async function toggleActive() {
    if (!record) return;
    // Панель после выключения закрывается — несохранённая правка пропала бы молча.
    if (dirty > 0 && !window.confirm('Несохранённые изменения пропадут. Продолжить?')) return;
    setError(null);
    try {
      if (active) await deactivate({ dict, id: String(record.id) }).unwrap();
      else await activate({ dict, id: String(record.id) }).unwrap();
      onSaved(
        active
          ? `${title} ${ui.feminine ? 'выключена' : 'выключен'} — в расчётах не участвует.`
          : `${title} снова действует.`,
      );
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось изменить состояние записи.'));
    }
  }

  function control(f: FieldDef) {
    const id = `${uid}-${f.name}`;
    const issue = showIssues ? issues[f.name] : undefined;
    const hint =
      dict === 'cost_items_1c' && f.name === 'distribution'
        ? (distributionHint(values.bdr_article_id, lineOptions) ?? f.hint)
        : f.hint;
    const describedBy =
      [hint ? `${id}-hint` : null, issue ? `${id}-error` : null].filter(Boolean).join(' ') ||
      undefined;
    const value = typeof values[f.name] === 'string' ? (values[f.name] as string) : '';
    if (f.type === 'checkbox') {
      return (
        <CheckLabel>
          <input
            id={id}
            type="checkbox"
            checked={values[f.name] === true}
            disabled={!editable || busy}
            onChange={(e) => set(f.name, e.target.checked)}
          />
          {f.label}
        </CheckLabel>
      );
    }
    const options =
      f.name === 'project_id'
        ? projectOptions
        : f.name === 'bdr_article_id'
          ? lines
          : (f.options ?? []);
    return (
      <>
        <FieldLabel htmlFor={id}>
          <span>
            {f.label}
            {f.required && (
              <span className="req" aria-hidden="true">
                {' '}
                *
              </span>
            )}
          </span>
        </FieldLabel>
        {f.type === 'select' ? (
          <Select
            id={id}
            value={value}
            disabled={!editable || busy}
            aria-invalid={issue ? true : undefined}
            aria-describedby={describedBy}
            onChange={(e) => set(f.name, e.target.value)}
          >
            <option value="" disabled={Boolean(f.required)}>
              {f.required ? '— выберите —' : '— не указано —'}
            </option>
            {renderOptions(options)}
          </Select>
        ) : (
          <Control
            id={id}
            type={f.type === 'date' ? 'date' : 'text'}
            inputMode={f.type === 'number' ? 'decimal' : undefined}
            autoComplete="off"
            placeholder={f.placeholder}
            value={value}
            readOnly={!editable}
            disabled={busy}
            aria-required={f.required ? true : undefined}
            aria-invalid={issue ? true : undefined}
            aria-describedby={describedBy}
            onChange={(e) => set(f.name, e.target.value)}
          />
        )}
        {hint && <FieldHint id={`${id}-hint`}>{typo(hint)}</FieldHint>}
        {issue && <FieldError id={`${id}-error`}>{issue}</FieldError>}
      </>
    );
  }

  const ruleHint = MATCH_RULE_HINTS[dict];

  return (
    <Drawer open onClose={close} labelledBy={`${uid}-title`} initialFocusRef={titleRef}>
      <DrawerHeader>
        <HeaderRow>
          <Titles>
            <Eyebrow>Справочник · {dictTitle}</Eyebrow>
            <DrawerTitle id={`${uid}-title`} ref={titleRef} tabIndex={-1}>
              {title}
            </DrawerTitle>
            {record && (
              <HeaderMeta>
                <FleetChip $tone={active ? 'success' : 'muted'}>
                  {statusLabel(dict, active)}
                </FleetChip>
              </HeaderMeta>
            )}
          </Titles>
          <DrawerCloseButton onClick={close} />
        </HeaderRow>
      </DrawerHeader>
      <DrawerBody>
        {error && (
          <Notice $tone="danger" role="alert">
            <span className="text">{error}</span>
          </Notice>
        )}
        {!editable && (
          <Notice $tone="info" role="note">
            <span className="text">
              {typo(
                'Только просмотр: менять справочники может сотрудник с правом записи в Экономисте.',
              )}
            </span>
          </Notice>
        )}
        <form id={`${uid}-form`} onSubmit={(e) => void submit(e)} noValidate>
          <FormGrid>
            {fields.map((f) => (
              <FormRow key={f.name} $wide={isWideField(f)}>
                {control(f)}
              </FormRow>
            ))}
          </FormGrid>
          {hasRule && ruleHint && (
            <RuleBox open={rule.trim() !== ''}>
              <summary>Правило классификации — необязательно</summary>
              <div className="body">
                <FieldHint id={`${uid}-rule-hint`}>
                  {typo(
                    `JSON-объект: по нему строки документов сами попадают в эту статью. Пусто — статью привязывают вручную. ${ruleHint.text}`,
                  )}
                </FieldHint>
                <ControlArea
                  id={`${uid}-rule`}
                  aria-label="Правило классификации, JSON"
                  aria-describedby={`${uid}-rule-hint`}
                  spellCheck={false}
                  value={rule}
                  readOnly={!editable}
                  disabled={busy}
                  placeholder={ruleHint.example}
                  onChange={(e) => setRule(e.target.value)}
                />
              </div>
            </RuleBox>
          )}
        </form>
      </DrawerBody>
      <DrawerFooter>
        {editable && record && (
          <Button
            type="button"
            $variant="ghost"
            disabled={busy}
            onClick={() => void toggleActive()}
            title={
              active
                ? 'Запись останется в справочнике, но в расчётах участвовать не будет'
                : 'Вернуть запись в расчёты'
            }
          >
            {active ? 'Выключить' : 'Включить'}
          </Button>
        )}
        <FooterStatus role="status">
          {busy ? 'Сохраняю…' : dirty > 0 ? `Изменено: ${dirty}` : ''}
        </FooterStatus>
        <Spacer />
        <Actions>
          <Button type="button" $variant="ghost" disabled={busy} onClick={close}>
            {editable ? 'Отмена' : 'Закрыть'}
          </Button>
          {editable && (
            <Button
              type="submit"
              form={`${uid}-form`}
              $variant="primary"
              disabled={busy || (record !== null && dirty === 0)}
            >
              {record ? 'Сохранить' : 'Добавить'}
            </Button>
          )}
        </Actions>
      </DrawerFooter>
    </Drawer>
  );
}
